import { MergeCellsOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { Button, ConfigProvider, Dropdown, Radio, Tooltip } from 'antd'
import { EXPLORER_BORDER_GRAY } from 'features/drilldown'
import {
  DrilldownPanelGroupType,
  DrilldownPanelType,
} from 'features/drilldown/types'
import React, { FC, ReactNode, useCallback } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import { useEnabledPanelsByGroup } from './panelConfigs/PanelConfigHooks'
import {
  DrilldownPanelConfig,
  DrilldownPanelInitialDataMap,
} from './panelConfigs/PanelConfigs'
import { BUTTON_GROUP_SPECS } from './panelConfigs/PanelGroupConfigs'

export type CreatePanelRequest<
  T extends DrilldownPanelType = DrilldownPanelType,
> = {
  panelType: T
  initialDataOverride?: DrilldownPanelInitialDataMap[T]
}

export interface CreatePanelButtonsProps {
  createPanels: (panelsToCreate: ReadonlyArray<CreatePanelRequest>) => void
  highlightType: DrilldownPanelGroupType | null
  onCollapseColumn?: () => void | undefined
  /**
   * Called whenever any group dropdown opens or closes. Lets a parent
   * keep this component visible (e.g. when wrapped in a hover-reveal zone)
   * while its portaled menu is open.
   */
  onDropdownOpenChange?: (open: boolean) => void
}

const CreatePanelButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledRadioButton = styled(Radio.Button)`
  &:hover {
    z-index: 10;
  }
`

const StyledDropdownButton = styled(Button)`
  .anticon {
    font-size: 16px !important;
  }
`

const RawCreatePanelButtons: FC<CreatePanelButtonsProps> = ({
  createPanels,
  highlightType,
  onCollapseColumn,
  onDropdownOpenChange,
}) => {
  const buttonsByGroup = useEnabledPanelsByGroup()

  const renderButtonGroup = useCallback(
    ([groupType, buttonsInGroup]: [
      DrilldownPanelGroupType,
      DrilldownPanelConfig[],
    ]) => {
      const { tooltip: groupTooltip, icon } = BUTTON_GROUP_SPECS[groupType]

      // Make a dropdown if there are multiple buttons in the group
      if (buttonsInGroup.length > 1) {
        const dropdownMenuItems: NonNullable<MenuProps['items']> =
          buttonsInGroup.flatMap((button, idx) => {
            const item = {
              key: button.name,
              onClick: () => createPanels([{ panelType: button.name }]),
              label: button.tooltip,
              icon: button.icon,
            }
            // Separate the blank "Table" panel from the preset tables.
            return groupType === 'table' && idx === 0
              ? [
                  item,
                  {
                    type: 'divider' as const,
                    key: `${button.name}-divider`,
                  },
                ]
              : [item]
          })

        return buildPanelButton(
          groupType as DrilldownPanelGroupType,
          () => {},
          groupTooltip ?? '',
          <Dropdown
            key={groupType}
            menu={{
              items: dropdownMenuItems,
            }}
            onOpenChange={onDropdownOpenChange}>
            <StyledDropdownButton type='text' icon={icon} />
          </Dropdown>,
          highlightType,
          true
        )
      }

      // For single-panel groups - no dropdown
      const button = buttonsInGroup[0]
      return buildPanelButton(
        button.name,
        () => createPanels([{ panelType: button.name }]),
        groupTooltip ?? button.tooltip,
        icon ?? button.icon,
        highlightType
      )
    },
    [createPanels, highlightType, onDropdownOpenChange]
  )

  return (
    <CreatePanelButtonsContainer>
      <Radio.Group size='large'>
        {buttonsByGroup.map(renderButtonGroup)}
        {onCollapseColumn &&
          buildPanelButton(
            'Collapse Column',
            onCollapseColumn,
            'Collapse Column',
            <MergeCellsOutlined />,
            null
          )}
      </Radio.Group>
    </CreatePanelButtonsContainer>
  )
}
export const CreatePanelButtons = React.memo(RawCreatePanelButtons)

const buildPanelButton = (
  type: DrilldownPanelType | DrilldownPanelGroupType | string,
  handleClick: () => void,
  tooltip: ReactNode,
  buttonElement: ReactNode,
  highlightType: DrilldownPanelGroupType | null,
  shouldUseDropdownPadding: boolean = false
) => {
  const isHighlighted = type === highlightType

  return (
    <ConfigProvider
      key={type}
      theme={{
        components: {
          Radio: {
            colorPrimary: isHighlighted
              ? CantileverTheme.colors.highVis
              : EXPLORER_BORDER_GRAY,
            colorPrimaryHover: CantileverTheme.colors.highVis,
          },
        },
      }}>
      <Tooltip title={tooltip}>
        <StyledRadioButton
          style={{
            ...(shouldUseDropdownPadding
              ? { paddingLeft: '5px', paddingRight: '5px' }
              : {}),
            ...(isHighlighted ? { zIndex: 10 } : {}),
          }}
          onClick={handleClick}
          checked={isHighlighted}>
          {buttonElement}
        </StyledRadioButton>
      </Tooltip>
    </ConfigProvider>
  )
}
